import { uniqBy } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type NavigationType,
  useLocation,
  useNavigationType,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import type { FilterAPI, SortingFieldName, SortingOrder } from '~/api/Catalog';
import { company } from '~/company/Company';
import { useDisclosure } from '~/hooks/useDisclosure';
import { useGlobal } from '~/hooks/useGlobal';
import { useMounted } from '~/hooks/useMounted';
import { useOnWarehouseChange } from '~/hooks/useOnWarehouseChange';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { catalogStore, orderStore } from '~/stores';
import type { ProductPropertyFilterCode } from '~/stores/CatalogStore';
import {
  DISCOUNTED_PARAM,
  isDiscountedFilterEnabled,
} from '~/stores/CatalogStore/discountedFilter';
import {
  CatalogCategory,
  ignoreParams,
} from '~/stores/CategoriesStore/CatalogCategory';
import resetScroll from '~/utils/resetScroll';

const OWN_QUERY_PARAMS = new Set([
  'sorting',
  'sortingDir',
  'priceFrom',
  'priceTo',
  'modal',
  'page',
  'showProducts',
  DISCOUNTED_PARAM,
]);

interface CategoryPageOptions {
  /** Leaves an open end of the price range open instead of filling it with the category bound. */
  keepOpenPriceEnds?: boolean;
}

export const useCategoryPage = ({
  keepOpenPriceEnds = false,
}: CategoryPageOptions = {}) => {
  const { subCat2Id } = useParams<{ subCat2Id: string }>();
  const category = CatalogCategory.getInstance(subCat2Id || '');
  const navType: NavigationType = useNavigationType();
  const { isMobile } = useGlobal();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    pathname,
    search,
    state,
  }: {
    pathname: string;
    search: string;
    state: {
      source?: string;
      isDeeplink?: boolean;
      clean_filters?: boolean;
      parent?: string;
      backLink?: string;
      isBack?: boolean;
    };
  } = useLocation();

  /**
   * Whether the reader arrived here by going back.
   *
   * Read once, on the first render of this navigation, and never again: the
   * catalog rewrites its own query string right after a pop lands, which flips
   * `navType` to `REPLACE` while the reader has not moved. The bottom bar
   * cannot always pop the browser stack, and marks its fallback navigation as
   * a return instead.
   */
  const isReturn = useRef(navType === 'POP' || Boolean(state?.isBack)).current;

  const [seoIndexed, setSeoIndexed] = useState(!search.length);
  const mobileSort = useDisclosure();
  const mobileFilters = useDisclosure();

  const requestPage = (page: number) => {
    if (category.paginationRequest.isLoading) {
      return;
    }

    if (typeof window !== 'undefined') {
      resetScroll({ isMobile });
    }

    category.paginationRequest.loadPage(page, orderStore.etaWarehouseCode);
  };

  const loadMore = () => {
    category.paginationRequest.loadMore(orderStore.etaWarehouseCode);
  };

  const handleResetFilters = (options?: { mobile?: boolean }): void => {
    const { mobile } = options || {};
    catalogStore.resetCatalogFilters();

    if (!mobile) {
      const showProducts = searchParams.get('showProducts');
      setSearchParams(showProducts ? { showProducts } : {}, {
        replace: true,
        state: { source: pathname },
      });
      category.paginationRequest.debounceLoadPage(
        1,
        orderStore.etaWarehouseCode,
      );
      resetScroll({ behavior: 'smooth', isMobile });
    }
  };

  const handleChangeFilter = (
    code: ProductPropertyFilterCode,
    value?: any,
    mobile?: boolean,
  ) => {
    catalogStore.applyCatalogFilter(code, value);

    if (!mobile) {
      setQueryParams();
      category.paginationRequest.debounceLoadPage(
        1,
        orderStore.etaWarehouseCode,
      );
      resetScroll({ behavior: 'smooth' });
    }
  };

  const handleApplyMobileFilters = () => {
    catalogStore.applyFiltersEventEmitter.emit('submit');
    setQueryParams();
    category.paginationRequest.debounceLoadPage(1, orderStore.etaWarehouseCode);
    resetScroll({ behavior: 'smooth' });
    mobileFilters.close();
  };

  const handleApplySorting = (sort: [SortingFieldName, SortingOrder]) => {
    catalogStore.applyCatalogSorting(sort);
    category.paginationRequest.debounceLoadPage(1, orderStore.etaWarehouseCode);
  };

  const handleApplyBadges = (
    id: string,
    code: ProductPropertyFilterCode,
    value: any,
  ) => {
    catalogStore.addCatalogBadge(id, code, value);
    category.paginationRequest.debounceLoadPage(1, orderStore.etaWarehouseCode);
  };

  const handleRemoveBadges = (id: string, code: ProductPropertyFilterCode) => {
    catalogStore.removeCatalogBadge(id, code);
    category.paginationRequest.debounceLoadPage(1, orderStore.etaWarehouseCode);
  };

  const getCanonicalPathname = () => {
    if (!pathname) {
      return company.config.canonical;
    }

    const type = searchParams.get('type');

    if (!type) {
      return `${company.config.canonical}${pathname}`;
    }
    return `${company.config.canonical}${pathname}?type=${type}`;
  };

  const getAppliedFilters = () => {
    let counter = 0;

    if (catalogStore.currentFilters.price) {
      counter++;
    }

    if (catalogStore.currentFilters.discountPrice) {
      counter++;
    }

    counter += Object.keys(catalogStore.currentFilters.properties).length;

    return counter;
  };

  const hasActiveFilters =
    !!catalogStore.currentFilters.price ||
    !!catalogStore.currentFilters.discountPrice ||
    Object.keys(catalogStore.currentFilters.properties).length > 0;

  const setQueryParams = () => {
    const modal = searchParams.get('modal');
    const filters = JSON.parse(JSON.stringify(catalogStore.currentFilters));
    const page = category.paginationRequest.currentPage;

    const showProducts = searchParams.get('showProducts');

    // Preserve tracking/marketing params that are in ignoreParams but not our own UI params
    const preservedParams: Record<string, string> = {};
    for (const key of ignoreParams) {
      if (OWN_QUERY_PARAMS.has(key)) {
        continue;
      }

      const value = searchParams.get(key);
      if (value != null) {
        preservedParams[key] = value;
      }
    }

    const combined = new URLSearchParams({
      ...preservedParams,
      ...(catalogStore.currentSorting[0] === catalogStore.defaultSorting[0] &&
      catalogStore.currentSorting[1] === catalogStore.defaultSorting[1]
        ? {}
        : {
            sorting: catalogStore.currentSorting[0],
            sortingDir: catalogStore.currentSorting[1],
          }),
      ...(filters.price?.from &&
      filters.price.from !== catalogStore.pricesminmax.priceMinPounds
        ? { priceFrom: filters.price?.from }
        : {}),
      ...(filters.price?.to &&
      filters.price?.to !== catalogStore.pricesminmax.priceMaxPounds
        ? { priceTo: filters.price?.to }
        : {}),
      ...filters.properties,
      ...(filters.discountPrice && isDiscountedFilterEnabled()
        ? { [DISCOUNTED_PARAM]: '1' }
        : {}),
      ...(modal ? { modal } : {}),
      ...(page > 1 && company.variant({ '2': true }) ? { page } : {}),
      ...(showProducts ? { showProducts } : {}),
    });
    setSearchParams(combined, {
      replace: true,
      state: { source: pathname, parent: state?.parent, backLink },
    });
  };

  useEffect(() => {
    const page = searchParams.get('page');
    if (page && +page !== 1) {
      catalogStore.page.current = +page;
    }
  }, []);
  useEffect(() => {
    analyticsService.track(AnalyticsEvent.CATALOG_CATEGORY_VISITED, {
      id: subCat2Id || '',
      source: 'homepage',
    });
  }, []);

  useEffect(() => {
    const { sorting, filters } =
      CatalogCategory.parseSearchParams(searchParams);
    const { price, properties, discountPrice } = filters || {};
    const { from: priceFrom, to: priceTo } = price || {};

    if (sorting) {
      catalogStore.applyCatalogSorting(sorting);
    }

    catalogStore.setCurrentFilters({
      properties: {
        ...(navType !== 'POP' ? catalogStore.currentFilters.properties : {}),
        ...properties,
      },
      ...(keepOpenPriceEnds && (priceFrom || priceTo) ? { price } : {}),
      // will be fixed by backend params
      ...(!keepOpenPriceEnds &&
      ((priceFrom && +priceFrom !== catalogStore.pricesminmax.priceMinPounds) ||
        (priceTo && +priceTo !== catalogStore.pricesminmax.priceMinPounds))
        ? {
            price: {
              from: +(
                priceFrom ??
                catalogStore.pricesminmax.priceMinPounds ??
                0
              ),
              to: +(priceTo ?? catalogStore.pricesminmax.priceMaxPounds ?? 0),
            },
          }
        : {}),
      ...(discountPrice && { discountPrice }),
    });

    setQueryParams();
  }, [category.data?.id, category.data?.subcategory, pathname, search]);

  useEffect(() => {
    if (
      !isReturn &&
      !state?.clean_filters &&
      !search.includes('modal') &&
      state?.source !== pathname &&
      !['POP', 'REPLACE'].includes(navType)
    ) {
      const { discountPrice } =
        CatalogCategory.parseSearchParams(searchParams).filters ?? {};

      catalogStore.resetSorting();
      catalogStore.setCurrentFilters({
        properties: {},
        ...(discountPrice && { discountPrice }),
      });
      catalogStore.page.current = 1;
      setQueryParams();
    }
  }, [JSON.stringify(catalogStore.currentFilters), state]);

  useEffect(() => {
    setQueryParams();
  }, [
    JSON.stringify(catalogStore.currentSorting),
    category.paginationRequest.currentPage,
  ]);

  useEffect(() => {
    setSeoIndexed(!search.length);
  }, [search]);

  useOnWarehouseChange(
    (code, isChanged) => {
      if (isChanged || !category.matches(code)) {
        category.paginationRequest.loadPage(1, code);
      }
    },
    [category],
  );

  const mounted = useMounted();

  const filters: FilterAPI[] = useMemo(() => {
    if (!category.data?.filters) {
      return [];
    }

    const uniqFilters = uniqBy(category.data?.filters, 'code');

    return uniqFilters.filter((filter) => {
      if (filter.code === 'express_delivery') {
        return mounted && orderStore.isExpressAvailableNow;
      }
      return true;
    });
  }, [category.data?.filters, mounted]);

  const expressDeliveryFilterItem = filters.find(
    (item) => item.code === 'express_delivery',
  );

  const filtersForMobile = useMemo<FilterAPI[]>(
    () => [
      {
        code: 'price',
        name: 'Price',
        showExpanded: true,
        type: 'selector',
        values: [],
      },
      ...(filters.filter(
        (item) => item.code !== 'express_delivery' && item.code !== 'type',
      ) || []),
    ],
    [filters],
  );

  const parent = state?.parent;
  const backLink = state?.backLink;

  const pageTitle = category.data?.metadataTitle
    ? `${category.data.metadataTitle} - `
    : '';
  const pageDescription = category.data?.metadataDescription
    ? `${category.data.metadataDescription} - `
    : '';
  const canonical = getCanonicalPathname();
  const robots = company.isEnabledIndexParametrizedUrls
    ? category.isNotFound
      ? 'noindex, nofollow'
      : 'index, follow'
    : seoIndexed && !category.isNotFound
      ? 'index, follow'
      : 'noindex, nofollow';

  return {
    subCat2Id,
    category,
    seoIndexed,
    mobileSort,
    mobileFilters,
    filters,
    expressDeliveryFilterItem,
    parent,
    backLink,
    pageTitle,
    pageDescription,
    canonical,
    robots,
    filtersForMobile,
    requestPage,
    loadMore,
    handleResetFilters,
    handleChangeFilter,
    handleApplyMobileFilters,
    handleApplySorting,
    handleApplyBadges,
    handleRemoveBadges,
    getCanonicalPathname,
    getAppliedFilters,
    hasActiveFilters,
  };
};
